import React, { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import { Helmet } from "react-helmet-async"
import ScrollablePage from "../../ui/ScrollablePage"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import { Action, Actions, Facts, Header, Overview } from "../../ui/PageHeader"
import Breadcrumbs from "../../ui/Breadcrumbs"
import IDCopy from "../../ui/IDCopy"
import ErrorMsg from "../../ui/ErrorMsg"
import {
  fetchUserAccountById,
  logout,
  selectAuth,
  selectUserAccountsById,
} from "./slice"
import styled from "styled-components"
import Paper from "../../ui/Paper"
import PaperTitle, { PaperFooter, TitleText } from "../../ui/PaperTitle"
import PropTypes from "prop-types"
import TableHeader from "../../ui/TableHeader"
import Table, { Cell, Row } from "../../ui/Table"
import TableHeaderRow from "../../ui/TableHeaderRow"
import Button from "../../ui/Button"
import {
  faKey,
  faUsers,
  faSignOut,
  faUserSecret,
} from "@fortawesome/free-solid-svg-icons"
import ChangePassword from "./ChangePassword"
import {
  fetchActions,
  selectActionsByContext,
  selectActionsByIds,
} from "../audit/slice"

import ActionsTable from "../audit/ActionsTable"
import UserActivity from "./UserActivity"
import RolesTable from "./RolesTable"
import Authed from "./Authed"
import Timestamp from "../../ui/Timestamp"
import { selectAdvancedMode } from "../settings/slice"
import { jwtDecode } from "jwt-decode"
import { DateTime } from "luxon"

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding-top: 1rem;
`

Container.propTypes = { children: PropTypes.node }

/**
 * Logged-in user profile page
 *
 * @returns {JSX.Element}
 * @constructor
 */
const Profile = () => {
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const advancedMode = useSelector(selectAdvancedMode)

  // const advancedMode = useSelector(selectAdvancedMode)
  const accountsByID = useSelector(selectUserAccountsById)
  const account = accountsByID[auth?.sub]

  const status = auth?._loadStatus

  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [tokenExpiry, setTokenExpiry] = useState(null)

  useEffect(() => {
    dispatch(fetchUserAccountById(auth?.sub))
    // For user-facing display of token expiry
    if (auth?.token) {
      const dt = DateTime.fromSeconds(jwtDecode(auth?.token)?.exp)
      setTokenExpiry(dt)
    }
  }, [auth])

  const handleResetPassword = () => {
    setIsResettingPassword(!isResettingPassword)
  }

  const handleCloseResetPassword = () => {
    setIsResettingPassword(false)
  }

  const handleLogout = async () => {
    await dispatch(logout(auth?.sub)).unwrap()
    const navigate = useNavigate()
    navigate("/login")
  }

  // Close on ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key == "Escape") {
        setIsResettingPassword(false)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  })

  return (
    <Layout>
      <NavMain />
      <Helmet>
        <title>Profile - Astrosite</title>
      </Helmet>
      <ScrollablePage>
        {status === "loading" && (
          <Backdrop>
            <LoadingSpinner />
          </Backdrop>
        )}
        <Header>
          <Overview>
            <h1>{account?.display_name}</h1>
            <Breadcrumbs>
              <IDCopy id={account?.id} showCopy />
            </Breadcrumbs>
          </Overview>
          <Facts>
            <dt>Created</dt>
            <dd>
              {account?.created_at && (
                <Timestamp utc={advancedMode} columnMode={false}>
                  {account?.created_at}
                </Timestamp>
              )}
            </dd>
            <dt>Last IP address</dt>
            <dd>{account?.last_ip_address}</dd>
            <dt>Your API Key</dt>
            <dd>
              <IDCopy truncateTo={24} id={auth.token} showCopy />
            </dd>
            {tokenExpiry != null && (
              <>
                <dt>API Key expires</dt>
                <dd>{tokenExpiry.toRelative()}</dd>
              </>
            )}
          </Facts>
          <Actions>
            <>
              <Action>
                <Button onClick={handleLogout} icon={faSignOut}>
                  Logout
                </Button>
              </Action>
              <Action>
                <Button onClick={handleResetPassword} icon={faKey}>
                  Reset password
                </Button>
              </Action>
              <Authed allowRoles={["admin"]}>
                <Action>
                  <Link to={`/accounts`}>
                    <Button icon={faUsers}>Manage users</Button>
                  </Link>
                </Action>
              </Authed>
              <Authed allowRoles={["org-admin", "admin"]}>
                <Action>
                  <Link to={`/accounts/access-logs?from=m30d&to=now`}>
                    <Button icon={faUserSecret}>Access log</Button>
                  </Link>
                </Action>
              </Authed>
            </>
          </Actions>
        </Header>
        <Container>
          <UserActivity id={account?.id} />
          <RolesTable id={account?.id} roles={account?.roles} />
        </Container>
        {status === "rejected" && (
          <ErrorMsg
            msg={`There is a problem fetching the user account with ID ${account?.id}`}
            showIcon={true}
          />
        )}
      </ScrollablePage>
      {isResettingPassword && (
        <ChangePassword
          id={account?.id}
          handleCancel={handleCloseResetPassword}
        />
      )}
    </Layout>
  )
}

// Renders a table of the user account roles
const Roles = ({ roles = [] }) => {
  return (
    <Paper>
      <Table>
        <TableHeaderRow>
          <TableHeader>Role Name</TableHeader>
          <TableHeader>Organisation</TableHeader>
          <TableHeader />
        </TableHeaderRow>
        <tbody>
          {roles.map((role, i) => (
            <Row key={i} zebra>
              <Cell>{role.role}</Cell>
              <Cell>{role.ctx || "-"}</Cell>
            </Row>
          ))}
        </tbody>
      </Table>
    </Paper>
  )
}

Roles.propTypes = {
  id: PropTypes.string.isRequired,
  roles: PropTypes.array,
}

// Renders a table of the user account activity
const Activity = ({ id }) => {
  const dispatch = useDispatch()

  // we get any matching actions for this account
  const actionMatches = useSelector((state) =>
    selectActionsByContext(state, `account/${id}`)
  )
  // We get the full objects from the ids the match
  const actions = useSelector((state) =>
    selectActionsByIds(state, actionMatches?.ids || [])
  )

  useEffect(() => {
    dispatch(
      fetchActions({
        filter: {
          by_id: id,
        },
        page: {
          offset: 0,
          limit: 2,
        },
        context: `account/${id}`,
      })
    )
  }, [id])

  return (
    <Paper>
      <PaperTitle>
        <TitleText loadingState={actionMatches?._loadStatus || "idle"}>
          Activity for {id}
        </TitleText>
      </PaperTitle>
      <ActionsTable actions={actions} hideUserAccount={true} />
      {actions.length != 0 &&
        actionMatches?.page?.total_count > actions?.length && (
          <PaperFooter>
            <span>
              showing {actions?.length} of {actionMatches?.page?.total_count}{" "}
              <Link to={`/actions?by_id=${id}`}>view all</Link>
            </span>
          </PaperFooter>
        )}
    </Paper>
  )
}

Activity.propTypes = {
  id: PropTypes.string.isRequired,
}

export default Profile
